/**
 * SideCart Directive
 * Matches TypeScript side-cart.directive.ts exactly
 * Handles side cart open/close animations and state management
 */
// @ts-nocheck
import { SideCartManager } from '@theme/side-cart-manager';
import CartItems from '@theme/cart-items';
import CartManager from '@theme/cart-manager';
import { countLimitedSwatchUnits, notifySwatchLimit, SWATCH_LIMIT } from '@theme/swatch-limit';

// Check if GSAP is available (load from CDN if needed)
function loadGSAP() {
  return new Promise((resolve) => {
    if (window.gsap) {
      resolve(window.gsap);
      return;
    }

    // Load GSAP from CDN
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
    script.onload = () => resolve(window.gsap);
    script.onerror = () => {
      console.warn('GSAP failed to load, using fallback animations');
      resolve(null);
    };
    document.head.appendChild(script);
  });
}

class SideCart {
  static selector = '.sideCart';
  static OPENED_CLASS_NAME = 'slideout--opened';
  static OPENED_BODY_CLASS_NAME = 'has-slideout';
  static PARCEL_SMALL_FREE_SHIPPING_THRESHOLD = 25000;
  static FIVE_K_FREE_SHIPPING_THRESHOLD = 500000;
  static FIVE_K_SHIPPING_RATES = [
    '',
    'white glove',
    'rugs',
    'marset flat rate',
    'parcel oversized',
  ];
  static FREE_SHIPPING_MESSAGE = 'Free Shipping: Enjoy free shipping on your order';

  static getFreeShippingProgress(cart) {
    const shippingTotals = {
      parcelSmall: 0,
      fiveK: 0,
    };
    const shippingTypes = {
      parcelSmall: false,
      fiveK: false,
    };
    (cart?.items || []).forEach((item) => {
      const shippingRate = String(item?.fulfillment?.shipping_rate || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
      const lineTotal = Number(item?.final_line_price ?? item?.line_price ?? 0);
      const safeLineTotal = Number.isFinite(lineTotal) ? lineTotal : 0;

      if (shippingRate === 'parcel small') {
        shippingTypes.parcelSmall = true;
        shippingTotals.parcelSmall += safeLineTotal;
      } else if (SideCart.FIVE_K_SHIPPING_RATES.includes(shippingRate)) {
        shippingTypes.fiveK = true;
        shippingTotals.fiveK += safeLineTotal;
      }
    });

    return [
      shippingTypes.parcelSmall && {
        key: 'parcel-small',
        accessibleLabel: 'Parcel small free delivery progress',
        total: shippingTotals.parcelSmall,
        threshold: SideCart.PARCEL_SMALL_FREE_SHIPPING_THRESHOLD,
      },
      shippingTypes.fiveK && {
        key: 'five-k',
        accessibleLabel: 'Five thousand dollar free delivery progress',
        total: shippingTotals.fiveK,
        threshold: SideCart.FIVE_K_FREE_SHIPPING_THRESHOLD,
      },
    ]
      .filter(Boolean)
      .map((group) => ({
        ...group,
        remaining: Math.max(group.threshold - group.total, 0),
        percentage: Math.min(Math.max((group.total / group.threshold) * 100, 0), 100),
      }));
  }

  static getActiveFreeShippingProgress(cart) {
    const groups = SideCart.getFreeShippingProgress(cart);
    return groups.find((group) => group.key === 'five-k')
      || groups.find((group) => group.key === 'parcel-small')
      || null;
  }

  constructor(hostElement) {
    this.host = hostElement;
    this.sideCartManager = SideCartManager.getInstance();
    this.cartManager = CartManager.getInstance();

    // Get elements - matches TypeScript line 22-31
    this.mask = document.querySelector('.sideCart__mask');
    this.headerCount = this.host.querySelector('.sideCart__headerCount');
    this.currentFocusElement = null;
    this.productHolder = this.host.querySelector('.sideCart__items');
    this.mobileCheckoutForm = this.host.querySelector('.sideCart__mobileCheckoutForm');
    this.expressCheckout = this.host.querySelector('[data-mobile-express-checkout]');
    this.installmentTerms = this.host.querySelector('.sideCart__mobileInstallments shopify-payment-terms');
    this.onMobileCheckoutSubmit = this.handleMobileCheckoutSubmit.bind(this);
    this.onExpressCheckoutClick = this.handleExpressCheckoutClick.bind(this);
    this.installmentCopyObserver = null;
    this.installmentCopyRetry = null;

    // Current cart state (will be updated via events)
    this.currentStatus = this.cartManager.getState();
    this.cartItems = null;
    this.gsap = null;

    this.init();
  }

  async init() {
    // Load GSAP
    this.gsap = await loadGSAP();

    // Direct conversion from TypeScript line 36-50
    if (this.productHolder) {
      this.cartItems = new CartItems(this.currentStatus.items, this.productHolder);
    }

    // Subscribe FIRST so onStatusChange is ready before any open() call
    this.unsubscribeStatus = this.sideCartManager.subscribe(this.onStatusChange.bind(this));

    // Subscribe to cart manager state changes (if RxJS available, otherwise use events)
    if (this.cartManager.stateChange$) {
      this.cartManager.stateChange$.subscribe(this.onCartChange.bind(this));
    } else {
      // Fallback: listen to cart:update events
      window.addEventListener('cart:update', () => {
        this.cartManager.get().then(cart => this.onCartChange(cart));
      });
    }

    // Now safe to open — subscriber is already registered above
    if (window.location.hash && window.location.hash === '#open-sidecart') {
      this.sideCartManager.open();
      console.log("run")
    }
    window.addEventListener('cart:update', this.onCartEmitUpdate.bind(this));

    const cart = this.cartManager.getState();
    this.updateSubTotal(cart);
    this.initInstallmentCopy();
  }

  bindEvents() {
    // Matches TypeScript line 56-60
    if (this.mask) {
      this.mask.addEventListener('click', this.onMaskClick.bind(this));
    }

    if (this.productHolder) {
      this.productHolder.addEventListener('scroll', this.onItemsScroll.bind(this));
    }

    this.mobileCheckoutForm?.addEventListener('submit', this.onMobileCheckoutSubmit);
    this.expressCheckout?.addEventListener('click', this.onExpressCheckoutClick, true);
  }

  unbindEvents() {
    // Matches TypeScript line 64-68
    if (this.mask) {
      this.mask.removeEventListener('click', this.onMaskClick);
    }

    if (this.productHolder) {
      this.productHolder.removeEventListener('scroll', this.onItemsScroll);
    }

    this.mobileCheckoutForm?.removeEventListener('submit', this.onMobileCheckoutSubmit);
    this.expressCheckout?.removeEventListener('click', this.onExpressCheckoutClick, true);
  }

  onMaskClick() {
    // Matches TypeScript line 71-73
    this.sideCartManager.close();
  }

  onStatusChange(status) {
    // Matches TypeScript line 76-89
    const { OPENED_CLASS_NAME } = SideCart;
    const hasOpenClass = this.host.classList.contains(OPENED_CLASS_NAME);

    if (status === 'opened' && !hasOpenClass) {
      this.host.classList.add(OPENED_CLASS_NAME);
      this.host.setAttribute('tabindex', '0');
      this.updateCartState();
      this.animateOpen().then(() => {
        this.bindEvents();
      });
    } else if (status === 'closed' && hasOpenClass) {
      this.host.setAttribute('tabindex', '-1');
      this.host.classList.remove(OPENED_CLASS_NAME);
      this.animateClose().then(() => {
        this.unbindEvents();
      });
    }
  }

  async updateCartState() {
    // Use CartManager.get() which handles /cart?view=variant
    try {
      const cart = await this.cartManager.get();
      this.onCartChange(cart);
    } catch (e) {
      console.error('Error fetching cart:', e);
    }
  }

  onCartChange(cart) {
    // Direct conversion from TypeScript line 93-106
    if (cart.item_count === 0) {
      this.disableElements();
      this.productHolder.classList.add('sideCart__items--empty');
    } else {
      this.enableElements();
      this.productHolder.classList.remove('sideCart__items--empty');
    }
    if (this.cartItems) {
      this.cartItems.update(cart.items);
    }

    this.updateHeaderCount(cart);
    this.updateSubTotal(cart);
    this.updateShippingProgress(cart);
    this.currentStatus = cart;
  }


  updateHeaderCount(cart) {
    // Matches TypeScript line 108-122 EXACTLY
    if (!this.headerCount || !cart?.items) return;

    let itemCount = 0;
    cart.items.forEach(item => {
      if (item?.properties?._rugBundlePrice) {
        itemCount += 1;
      } else if (item?.properties?._rugBundleId) {
        return; // Skip hidden items
      } else {
        itemCount += item.quantity;
      }
    });

    const nbInCart = itemCount || 0;
    this.headerCount.innerHTML = `(${nbInCart})`;
    this.host.querySelectorAll('.sideCart__mobileCtaCount').forEach(el => {
      el.textContent = String(nbInCart);
    });
    this.host.querySelectorAll('.sideCart__mobileCtaNoun').forEach(el => {
      el.textContent = nbInCart === 1 ? 'Item' : 'Items';
    });
  }

  updateSubTotal(cart) {
    // Matches TypeScript line 124-126
    if (!cart) return;

    // Format money (simplified - use Shopify formatMoney if available)
    const formattedPrice = this.formatMoney(cart.total_price);
    this.host.querySelectorAll('.sideCart__mobileTotal, .sideCart__mobileCtaTotal').forEach(el => {
      el.textContent = formattedPrice;
    });
  }

  updateShippingProgress(cart) {
    const container = this.host.querySelector('[data-shipping-progress]');
    if (!container) return;

    const group = SideCart.getActiveFreeShippingProgress(cart);
    container.hidden = !group;
    if (!group) {
      container.replaceChildren();
      return;
    }

    const groupElement = document.createElement('div');
    groupElement.className = 'sideCart__shippingProgressGroup';
    groupElement.dataset.shippingProgressGroup = group.key;
    groupElement.setAttribute('aria-label', group.accessibleLabel);

    const copy = document.createElement('div');
    copy.className = 'sideCart__shippingProgressCopy';

    if (group.remaining === 0) {
      copy.classList.add('sideCart__shippingProgressCopy--success');
      const check = document.createElement('span');
      check.className = 'sideCart__shippingProgressCheck';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '\u2713';
      const message = document.createElement('span');
      message.textContent = SideCart.FREE_SHIPPING_MESSAGE;
      copy.append(check, message);
    } else {
      const message = document.createElement('span');
      message.append('You\'re ');
      const remaining = document.createElement('strong');
      remaining.textContent = this.formatMoney(group.remaining);
      message.append(remaining, ' away from free delivery');

      const target = document.createElement('span');
      target.className = 'sideCart__shippingProgressTarget';
      target.textContent = this.formatMoney(group.threshold);
      copy.append(message, target);
    }

    const track = document.createElement('div');
    track.className = 'sideCart__shippingProgressTrack';
    track.setAttribute('role', 'progressbar');
    track.setAttribute('aria-label', `Progress toward ${this.formatMoney(group.threshold)} free delivery`);
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', '100');
    track.setAttribute('aria-valuenow', String(Math.round(group.percentage)));

    const fill = document.createElement('span');
    fill.className = 'sideCart__shippingProgressFill';
    fill.style.setProperty('--shipping-progress', `${group.percentage}%`);
    track.appendChild(fill);
    groupElement.append(copy, track);
    container.replaceChildren(groupElement);
  }

  // formatMoney(cents) {
  //   // Simple money formatter - matches Shopify's formatMoney utility
  //   if (window.Shopify && window.Shopify.formatMoney) {
  //     return window.Shopify.formatMoney(cents);
  //   }

  //   // Fallback formatter
  //   const dollars = (cents / 100).toFixed(2);
  //   return `$${dollars}`;
  // }
  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  }

  async initInstallmentCopy() {
    if (!this.installmentTerms) return;

    try {
      await customElements.whenDefined('shopify-payment-terms');
    } catch (error) {
      return;
    }

    this.observeInstallmentCopy();
  }

  observeInstallmentCopy(attempt = 0) {
    const shadowRoot = this.installmentTerms?.shadowRoot;

    // Shopify attaches and renders the component asynchronously.
    if (!shadowRoot) {
      if (attempt < 100) {
        this.installmentCopyRetry = window.setTimeout(() => {
          this.observeInstallmentCopy(attempt + 1);
        }, 50);
      }
      return;
    }

    this.applyInstallmentCopy(shadowRoot);
    this.installmentCopyObserver?.disconnect();
    this.installmentCopyObserver = new MutationObserver(() => {
      this.applyInstallmentCopy(shadowRoot);
    });
    this.installmentCopyObserver.observe(shadowRoot, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  applyInstallmentCopy(shadowRoot) {
    const content = shadowRoot.querySelector('#shopify-installments-content');
    if (!content) return;

    const textNodes = [...content.childNodes].filter(node => node.nodeType === Node.TEXT_NODE);
    const prefixNode = textNodes.find(node => /^\s*From\s+/i.test(node.textContent || ''));
    const affirmContextNode = textNodes.find(node => /\/mo\s+with\s+Affirm\s+or\s*$/i.test(node.textContent || ''));

    if (prefixNode) {
      prefixNode.textContent = prefixNode.textContent.replace(/^\s*From\s+/i, 'As low as ');
    }

    if (affirmContextNode) {
      affirmContextNode.textContent = affirmContextNode.textContent.replace(/\s*Affirm\s+or\s*$/i, ' ');
    }

    const shopLogo = content.querySelector('svg[role="img"]')?.parentElement;
    if (shopLogo && !content.querySelector('[data-abc-installment-suffix]')) {
      const suffix = document.createElement('span');
      suffix.dataset.abcInstallmentSuffix = '';
      suffix.textContent = ' Pay Installments';
      shopLogo.after(suffix);
    }
  }

  getCheckoutAnalyticsData(checkoutType, paymentMethod) {
    const cart = this.currentStatus || this.cartManager.getState() || {};
    const visibleItems = (cart.items || []).filter(item => !item.hidden && item.product_id != 7704109711498);
    const basketSize = visibleItems.reduce((count, item) => count + Number(item.quantity || 0), 0);

    return {
      checkout_type: checkoutType,
      payment_method: paymentMethod,
      basket_size: basketSize,
      basket_size_bucket: basketSize >= 3 ? '3+' : String(basketSize),
      cart_value: Number(cart.total_price || 0) / 100,
      currency: window.Shopify?.currency?.active || 'USD'
    };
  }

  trackCheckoutEntry(checkoutType, paymentMethod = 'standard') {
    const eventData = this.getCheckoutAnalyticsData(checkoutType, paymentMethod);
    window.gtag?.('event', 'cart_checkout_entry', eventData);

    const publishResult = window.Shopify?.analytics?.publish?.(
      'abc_home:cart_checkout_entry',
      eventData
    );
    publishResult?.catch?.(() => {});
  }

  handleMobileCheckoutSubmit(event) {
    if (countLimitedSwatchUnits(this.currentStatus?.items) > SWATCH_LIMIT) {
      event.preventDefault();
      notifySwatchLimit();
      return;
    }
    this.trackCheckoutEntry('standard', 'checkout');
  }

  handleExpressCheckoutClick(event) {
    if (countLimitedSwatchUnits(this.currentStatus?.items) > SWATCH_LIMIT) {
      event.preventDefault();
      event.stopPropagation();
      notifySwatchLimit();
      return;
    }

    const paymentElements = {
      'SHOP-PAY-WALLET-BUTTON': 'shop_pay',
      'SHOPIFY-APPLE-PAY-BUTTON': 'apple_pay',
      'SHOPIFY-PAYPAL-BUTTON': 'paypal',
      'SHOPIFY-GOOGLE-PAY-BUTTON': 'google_pay',
      'SHOPIFY-AMAZON-PAY-BUTTON': 'amazon_pay'
    };
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const paymentElement = path.find(node => node?.tagName && paymentElements[node.tagName]);
    const paymentMethod = paymentElements[paymentElement?.tagName] || 'express_other';
    this.trackCheckoutEntry('express', paymentMethod);
  }

  async animateOpen() {
    // Matches TypeScript line 133-156
    return new Promise((resolve) => {
      const { mask } = this;
      this.currentFocusElement = document.querySelector('*:focus');
      document.body.classList.add(SideCart.OPENED_BODY_CLASS_NAME);

      this.host.setAttribute('aria-expanded', 'true');

      if (this.gsap) {
        // Use GSAP animations
        this.gsap.killTweensOf(mask);
        this.gsap.killTweensOf(this.host);

        this.gsap.to(mask, {
          duration: 0.625,
          autoAlpha: 1,
          display: 'block'
        });

        this.gsap.to(this.host, {
          duration: 0.625,
          x: 0,
          ease: 'power4.out',
          onComplete: resolve
        });
      } else {
        // Fallback CSS animations
        if (mask) {
          mask.style.opacity = '1';
          mask.style.display = 'block';
        }
        this.host.style.transform = 'translateX(0)';
        setTimeout(resolve, 625);
      }
    });
  }

  async animateClose() {
    // Matches TypeScript line 159-186
    return new Promise((resolve) => {
      const { mask } = this;

      this.host.setAttribute('aria-expanded', 'false');

      if (this.gsap) {
        // Use GSAP animations
        this.gsap.killTweensOf(mask);
        this.gsap.killTweensOf(this.host);

        document.body.classList.remove(SideCart.OPENED_BODY_CLASS_NAME);

        this.gsap.to(this.host, {
          duration: 0.5,
          x: '100%',
          ease: 'power4.out'
        });

        this.gsap.to(mask, {
          duration: 0.5,
          autoAlpha: 0,
          display: 'none',
          onComplete: () => {
            if (this.currentFocusElement) {
              this.currentFocusElement.focus();
            }
            resolve();
          }
        });
      } else {
        // Fallback CSS animations
        document.body.classList.remove(SideCart.OPENED_BODY_CLASS_NAME);
        this.host.style.transform = 'translateX(100%)';
        if (mask) {
          mask.style.opacity = '0';
          setTimeout(() => {
            mask.style.display = 'none';
            if (this.currentFocusElement) {
              this.currentFocusElement.focus();
            }
            resolve();
          }, 500);
        } else {
          resolve();
        }
      }
    });
  }

  disableElements() {
    // Matches TypeScript line 188-193
    const toDisable = document.querySelectorAll('[disable-when-empty]');
    toDisable.forEach(el => {
      el.setAttribute('disable-when-empty', 'disabled');
    });
  }

  enableElements() {
    // Matches TypeScript line 195-200
    const toEnable = document.querySelectorAll('[disable-when-empty]');
    toEnable.forEach(el => {
      el.setAttribute('disable-when-empty', '');
    });
  }

  onItemsScroll(e) {
    // Matches TypeScript line 220-226
    if (!this.productHolder) return;

    if (e.target.scrollTop > 0) {
      this.productHolder.classList.add('sideCart__items--scrolling');
    } else {
      this.productHolder.classList.remove('sideCart__items--scrolling');
    }
  }

  async onCartEmitUpdate() {
    try {
      const cart = await this.cartManager.get();
      this.onCartChange(cart);
    } catch (e) {
      console.error('Error updating cart on cart:update:', e);
      return;
    }

    // Already open — nothing to do (add-to-cart-handler opens synchronously on add)
    if (this.sideCartManager.getStatus() === 'opened') {
      return;
    }

    // Ignore stale cart:update callbacks that finish after the user closed the drawer
    if (this.sideCartManager.wasRecentlyClosedByUser()) {
      return;
    }

    this.sideCartManager.open();
  }

  destroy() {
    // Cleanup
    if (this.unsubscribeStatus) {
      this.unsubscribeStatus();
    }
    window.removeEventListener('cart:update', this.onCartEmitUpdate);
    window.clearTimeout(this.installmentCopyRetry);
    this.installmentCopyObserver?.disconnect();
    this.unbindEvents();
  }
}

// Initialize SideCart on DOM ready
function initSideCart() {
  const sideCartElement = document.querySelector(SideCart.selector);
  if (sideCartElement) {
    window.sideCartInstance = new SideCart(sideCartElement);
  }
}
// Initialize on DOM ready and section load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSideCart);
} else {
  initSideCart();
}

// Re-initialize on section load (Shopify sections)
document.addEventListener('shopify:section:load', initSideCart);
// Export for module usage
export default SideCart;

